import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import {useTranslation} from "react-i18next";
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment/moment';
import {ControlGender} from '../../controls/agyle/general';
import AgyleTextField from '../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import MessageBox from '../../util/messageBox';
import API from '../../app/api';
import {setSnackbar} from '../../store/reducers/globals';
import {useDispatch, useSelector} from 'react-redux';
import config from '../../app/config';
import {hasPermission, Permissions} from '../../store/reducers/user';
import {gendersDict} from '../../store/reducers/dicts';


const useStyles = makeStyles((theme) => ({
    contentText: {
        textAlign: "left",
        fontSize: "1.4rem",
        fontWeight: "600",
        "&.big":{
            fontSize: "3rem",
        }
    },
    divRow: {
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "column",
        alignItems: "flex-start",
        color: "grey"
    },
    value: {
        fontWeight: "bold",
        color: "white",
        display: "inline"
    },
    dateTextField: {
        minHeight: "47px"
    },
    controlGender: {
        maxHeight: "47px"
    }
}));

const AdminPatientEyonix = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const canCreatePatient = dispatch(hasPermission(Permissions.CAN_CREATE_PATIENT));
    const canEditPatient = dispatch(hasPermission(Permissions.CAN_EDIT_PATIENT));

    const patientBase = {
        firstName: "",
        lastName: "",
        middleNames: "",
        dob: "",
        mrn: "", // MRN
        gender: "",
        phoneNumber: "",
        addressLine1: "",
        city:"",
        stateOrProvince: "",
        country:"",
        postCode: "",
        street: "",
        cellPhone: "",
        emailAddress: "",
        fax: "",
    }

    const [patient, setPatient] = useState(patientBase);
    const [isError, setIsError] = useState(false);
    const errorPicker = useRef(null);
    const [disabled, setDisabled] = useState(false);
    const [dialogConfirmOpen, setDialogConfirmOpen] = useState(false);
    const [mrnNumberFree, setMrnNumberFree] = React.useState(false);

    const [isNew, setNew] = React.useState(true);

    const genderDict = useSelector(gendersDict);

    useEffect(() => {
        if (props.patient && genderDict) {
            const patient = props.patient;
            const patientBase = {
                firstName: patient.firstName,
                lastName: patient.familyName,
                middleNames: patient.middleNames,
                dob: moment(patient.dob),
                mrn: patient.__id, // MRN
                gender: _.find(genderDict, (g) => g.__code === patient.genderCode), // what to put here: string? dict object?
                phoneNumber: _.find(patient.phoneNumbers, (p) => p.numberType === 'Other')?.number || '',
                addressLine1: patient.addressLines[0] || _.find(patient.addresses, (a) => a.typeName === 'H')?.addressLines[0] || '',
                city: patient.addressLines[1] || _.find(patient.addresses, (a) => a.typeName === 'H')?.addressLines[1] || '',
                stateOrProvince: patient.addressLines[2] || _.find(patient.addresses, (a) => a.typeName === 'H')?.addressLines[2] || '',
                country: patient.addressLines[3] || _.find(patient.addresses, (a) => a.typeName === 'H')?.addressLines[3] || '',
                postCode: patient.postalCode,
                street: '',
                cellPhone: _.find(patient.phoneNumbers, (p) => p.numberType === 'Mobile')?.number || '',
                emailAddress: patient.emailAddress,
                fax: _.find(patient.phoneNumbers, (p) => p.numberType === 'Fax')?.number || '',
                __version: patient.__version,
            };
            setPatient(patientBase);
            setNew(false);
        }
    }, [props.patient, genderDict]);

    const renderMobileBody = () => {
        return (
            <>
                <div style={{width: 400, marginBottom: 10}}>
                    {t("Confirm Patient Data: ")}
                </div>
                <div className={classes.divRow}>
                    <div>First Name: <div className={classes.value}>{patient.firstName}</div></div>
                    {patient.middleNames && <div>Middle Name: <div className={classes.value}> {patient.middleNames}</div></div>}
                    <div>Last Name: <div className={classes.value}>{patient.lastName}</div></div>
                    <div>MRN: <div className={classes.value}>{patient.mrn} </div></div>
                    <div>Date of birth: <div className={classes.value}>{moment(patient.dob).format("DD/MM/YYYY")}</div></div>
                    <div>Gender: <div className={classes.value}>{patient.gender?.__description}</div></div>
                    {patient.addressLine1 && <div>Address Line: <div className={classes.value}>{patient.addressLine1}</div></div>}
                    {patient.city && <div>City: <div className={classes.value}>{patient.city}</div></div>}
                    {patient.stateOrProvince && <div>State or Province: <div className={classes.value}>{patient.stateOrProvince}</div></div>}
                    {patient.country && <div>Country: <div className={classes.value}>{patient.country}</div></div>}
                    {patient.postCode && <div>Zip Code: <div className={classes.value}>{patient.postCode}</div></div>}
                    {patient.cellPhone && <div>Cell Phone: <div className={classes.value}>{patient.cellPhone}</div></div>}
                    {patient.phoneNumber && <div>Phone: <div className={classes.value}>{patient.phoneNumber}</div></div>}
                    {patient.emailAddress && <div>Email: <div className={classes.value}>{patient.emailAddress}</div></div>}
                    {patient.fax && <div>Fax: <div className={classes.value}>{patient.fax}</div></div>}
                </div>
            </>
        );
    };

    const checkDisabled = () => {
        return (!canCreatePatient && !canEditPatient) || !(patient.firstName && patient.lastName && patient.mrn && patient.dob && patient.dob.isValid() && patient.dob.isSameOrBefore(moment())
            && patient.gender && (!props.phoneMandatory || (patient.cellPhone || patient.phoneNumber))) ||
            (!isNew && !canEditPatient)
    }

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setPatient({
            ...patient,
            [name]: value,
        });
    };

    const handleAddPatient = () => {
        let patientObj = {
            "__id": patient.mrn,
            //"incomingSystem": "EYONIX",
            "hospitalNumber": patient.mrn,
            "patientNumbers" : [ {
                "number" : patient.mrn,
                "type" : "MRN",
                "simpleType" : "MRN",
                "incomingSystem" : "EYONIX"
            } ],
            "firstName" : patient.firstName,
            "middleNames" : patient.middleNames,
            "familyName" : patient.lastName,
            "dob" : moment(patient.dob).format("YYYY-MM-DD")+"T00:00:00.000Z",
            "gender" : patient.gender.__description,
            "genderCode" : patient.gender.__code,
            "addressLines" : [patient.addressLine1, patient.city, patient.stateOrProvince, patient.country ],
            "postalCode" : patient.postCode,
            "addresses" : [ {
                "typeName" : "H",
                "addressLines" : [patient.addressLine1, patient.city, patient.stateOrProvince, patient.country ],
                "postalCode": patient.postCode,
            } ],
            "alerts" : null,
            "emails" : [{
                "emailAddress" : patient.emailAddress,
                "__type" : "Personal"
            }],
            "emailAddress": patient.emailAddress,
            "phoneNumbers" : [ {
                "number" : patient.cellPhone,
                "numberType" : "Mobile",
            },
                {
                "number" : patient.phoneNumber,
                "numberType" : "Other",
            },
                {
                    "number" : patient.fax,
                    "numberType" : "Fax",
                }
            ],
        }

        API.addPatient(patientObj).then(patient => {
                dispatch(setSnackbar({type: "success", message: "Patient added!"}));
                setDialogConfirmOpen(false);
                props.closeModal();
            })
            .catch(err => {
                if(err && err.response && err.response.data) {
                    if(err.response.data.indexOf("Patient already exists") > -1 && err.response.data.indexOf(patientObj.mrn)) {
                        dispatch(setSnackbar({open: true, message: `Patient with MRN number ${patient.mrn} already exists` , severity: "error", type: 'error'}));
                        return;
                    }
                }
                dispatch(setSnackbar({type: "error", message: "Patient not added!"}));
            });
    }

    const handleEditPatient = () => {
        let updateObj = {
            //"safeguard": true,
            "firstName": patient.firstName,
            "middleNames": patient.middleNames,
            "familyName": patient.lastName,
            "dob": moment(patient.dob).format("YYYY-MM-DD") + "T00:00:00.000Z",
            "genderId": patient.gender.__id,
            "addressLines": [patient.addressLine1, patient.city, patient.stateOrProvince, patient.country],
            "postalCode": patient.postCode,
            "addresses": [
                {
                    "typeName": "H",
                    "addressLines": [patient.addressLine1, patient.city, patient.stateOrProvince, patient.country],
                    "postalCode": patient.postCode,
                }
            ],
            "emailAddress": patient.emailAddress,
            "emails": [
                {
                    "emailAddress": patient.emailAddress,
                }
            ],
            "phoneNumbers": [
                {
                    "number": patient.cellPhone,
                    "numberType": "Mobile",
                },
                {
                    "number": patient.phoneNumber,
                    "numberType": "Other",
                },
                {
                    "number": patient.fax,
                    "numberType": "Fax",
                }
            ],
        }

        API.updatePatient({patientId: props.patient.__id, data: updateObj})
            .then((data) => {
                if(data.success) {
                    dispatch(setSnackbar({ type: 'success', message: 'Patient updated!' }));
                    setDialogConfirmOpen(false);
                    props.closeModal(true);
                } else {
                    dispatch(setSnackbar({ open: true, message: `Patient not updated! ${data.errorCode}`, severity: 'error', type: 'error' }));
                }
            })
            .catch((err) => {
                dispatch(setSnackbar({ open: true, message: 'Patient not updated!', severity: 'error', type: 'error' }));
            });
    };

    const confirmAndCheckPatientId = () => {
        setMrnNumberFree(false);

        // if patient is not new, we don't need to check MRN number, just confirm the data
        if (!isNew) {
            setDialogConfirmOpen(true);
            return
        }

        let o = {
            resourceUri: "/patients",
            text: patient.mrn,
            page:1,
            pageSize: 20,
            options:{}
        }
        API.getSearchList(o).then(res => {
            if (res.status === 200) {
                setDialogConfirmOpen(true);
                if((_.findIndex(res.data.collection['__data'], (p) => p.__id == patient.mrn)) > -1) {
                    dispatch(setSnackbar({open: true, message: `Patient with MRN number ${patient.mrn} already exists` , severity: "error", type: 'error'}));
                    //console.debug("API.getSearchList request status : " + JSON.stringify(res.data));
                    setMrnNumberFree(true);
                } else {
                    setMrnNumberFree(false);
                }
            }
            if (res.status === 204) {
                setMrnNumberFree(false);
                setDialogConfirmOpen(true);
            }
            if (res.status !== 200 && res.status !== 204) {
                setMrnNumberFree(true);
                dispatch(setSnackbar({type: "error", message: "Error on MRN number search!"}));
                console.debug("API.getSearchList request status : " + res.status);
            }
        });

    }

    return(<div>
        <Grid container direction={"row"} spacing={2} style={{height: "90%"}} alignContent={"flex-start"}>
            <Grid item xs={12} sx={{mt: 3}}>
                <Typography className={classes.contentText}>
                    {t("Patient Information")}
                </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
                    <AgyleTextField
                        size={"medium"}
                        style={{width: "100%"}}
                        label={t("First Name")}
                        onChange={handleChangeInput}
                        name="firstName"
                        value={patient.firstName}
                        variant="outlined"
                        required
                        disabled={disabled}
                        error={_.isEmpty(patient.firstName) && isError}
                    />
            </Grid>
            <Grid item xs={12} sm={4}>
                    <AgyleTextField
                        size={"medium"}
                        style={{width: "100%"}}
                        label={t("Middle Name")}
                        onChange={handleChangeInput}
                        name="middleNames"
                        value={patient.middleNames}
                        disabled={disabled}
                        variant="outlined"
                        error={_.isEmpty(patient.middleNames) && isError}
                    />
            </Grid>
            <Grid item xs={12} sm={4}>
                    <AgyleTextField
                        size={"medium"}
                        style={{width: "100%"}}
                        label={t("Last Name")}
                        disabled={disabled}
                        onChange={handleChangeInput}
                        value={patient.lastName}
                        name="lastName"
                        variant="outlined"
                        required
                        error={_.isEmpty(patient.lastName) && isError}
                        color="tertiary"
                    />
            </Grid>
            <Grid item xs={12} sm={4}>
                    <AgyleTextField
                        size={"medium"}
                        style={{width: "100%"}}
                        label={t("MRN")}
                        name="mrn"
                        disabled={disabled || !isNew}
                        onChange={handleChangeInput}
                        value={patient.mrn}
                        required
                        error={_.isEmpty(patient.mrn) && isError}

                    />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleDatePicker
                    size={"small"}
                    textFieldClass={classes.dateTextField}
                    onChange={(e) =>
                        setPatient((prevState) => ({
                            ...prevState,
                            dob: e,
                        }))
                    }
                    type="date"
                    disabled={disabled}
                    label={t("Date of birth")}
                    maxDate={moment()}
                    required
                    value={patient.dob}
                    error={!patient.dob}
                />

            </Grid>

            <Grid item xs={12} sm={4}>
                <ControlGender
                    textFieldClass={classes.controlGender}
                    agyleRefresh
                    label={t("Gender")}
                    required
                    size={"large"}
                    disabled={disabled}
                    value={patient.gender}
                    onChange={value => {
                        setPatient((pr) =>
                            ({
                                ...pr,
                                gender: value,
                            }));
                    }}
                    color="tertiary"
                />
            </Grid>

            <Grid item xs={12} sx={{mt: 3}}>
                <Typography className={classes.contentText}>
                    {t("Contact Information")}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Address Line 1')}
                    value={patient.addressLine1}
                    name="addressLine1"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('City')}
                    value={patient.city}
                    name="city"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('State or Province')}
                    value={patient.stateOrProvince}
                    name="stateOrProvince"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Country')}
                    value={patient.country}
                    name="country"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Zip Code')}
                    value={patient.postCode}
                    name="postCode"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Cell Phone')}
                    value={patient.cellPhone}
                    name="cellPhone"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Phone')}
                    value={patient.phoneNumber}
                    name="phoneNumber"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Email')}
                    value={patient.emailAddress}
                    name="emailAddress"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AgyleTextField
                    size={"large"}
                    disabled={disabled}
                    fullWidth
                    label={t('Fax')}
                    value={patient.fax}
                    name="fax"
                    onChange={handleChangeInput}
                    style={{marginBottom: 10}}

                />
            </Grid>
            {props.phoneMandatory &&
                <Grid item xs={12} sx={{mt: 3}}>
                    {t("* Indicates required field. At least one phone number is required")}
                </Grid>
            }
            <Grid item xs={12} sx={{mt: 3}}>
                <div style={{float: "right"}}>
                    <AgyleButton
                        onClick={() => confirmAndCheckPatientId()}
                        variant="contained"
                        size="large"
                        disabled={checkDisabled()}
                    >
                        {isNew ? t("ADD PATIENT") : t("UPDATE PATIENT")}
                    </AgyleButton>
                </div>
            </Grid>


        </Grid>
        <MessageBox
            onClose={() => setDialogConfirmOpen(false)}
            title={t("Confirmation")}
            messageObject={renderMobileBody()}
            haveAccept={{ yes: t("Confirm"), no: t("Close") }}
            onAccept={() => isNew ? handleAddPatient() : handleEditPatient()}
            open={dialogConfirmOpen}
            disabled={mrnNumberFree}
            disableOnAccept
        />
        </div>)
}

export default AdminPatientEyonix